import h from 'virtual-dom/h';
import createElement from 'virtual-dom/create-element';

export interface StarRatingOptions {
    labelElementId: string;
    label: string;

    starsElementId: string;
    starInactiveClass?: string;
    starActiveClass?: string;
    starColorClass?: string;
    totalAmountOfStars: number;
    selectedAmountOfStars: number;

    buttonElementId?: string;
    buttonResetClass?: string;
    buttonResetText?: string;

    clickableClass?: string;
}

export class StarRating {
    public options: StarRatingOptions;
    public rootVirtualElement: VirtualDOM.VNode; // The virtual root element of the star rating
    public rootDomElement: Element; // The actual root dom element of the star rating

    constructor(options: StarRatingOptions) {
        this.options = options;

        this.options.starActiveClass = this.options.starActiveClass || 'icon icon-normal icon-star';
        this.options.starInactiveClass = this.options.starInactiveClass || 'icon icon-normal icon-star disabled';
        this.options.starColorClass = this.options.starColorClass || 'star-colored';
        this.options.buttonResetClass = this.options.buttonResetClass || 'btn btn-normal btn-gray';
        this.options.buttonResetText = this.options.buttonResetText || 'Reset';
        this.options.clickableClass = this.options.clickableClass || 'clickable';

        this.render();
    }

    protected element(elementId: string) {
        let element = document.getElementById(elementId);
        if (element) {
            return element;
        }

        throw new Error(`No element with id ${elementId} could be found`);
    }

    /**
     * Creates a root virtual element which holds the rendered items
     * Return the default wrapper element
     */
    protected createVirtualRootElement(elements: (string | VirtualDOM.VNode)[]): VirtualDOM.VNode {
        return this.wrapperElement(elements);
    }

    /**
     * Default wrapper element generator
     * Generates a div element
     */
    protected wrapperElement(elements: (string | VirtualDOM.VNode)[]): VirtualDOM.VNode {
        return h('div', {}, elements);
    }

    protected setRating(rating: number) {
        this.options.selectedAmountOfStars = rating;
        this.rerender();
    }

    protected renderLabel(): void {
        let baseElement = this.element(this.options.labelElementId);

        let label = h('span', {}, this.options.label);

        let virtualElement = this.createVirtualRootElement([label]);
        let domElement = createElement(virtualElement);

        baseElement.appendChild(domElement);
    }

    protected renderStars(): void{
        let baseElement = this.element(this.options.starsElementId);

        let stars: VirtualDOM.VNode[] = [...Array(this.options.totalAmountOfStars)].map((_: any, i: number) =>
            h('i', {
                className: `${this.options.starColorClass} ${this.options.clickableClass} ` + (i < this.options.selectedAmountOfStars
                    ? this.options.starActiveClass
                    : this.options.starInactiveClass),
                onclick: () => {
                    this.setRating(i + 1);
                }
            }, [])
        );

        let virtualElement = this.createVirtualRootElement(stars);
        let domElement = createElement(virtualElement);

        baseElement.appendChild(domElement);
    }

    protected renderButton(): void {
        let baseElement = this.element(this.options.buttonElementId!);

        let clearBtn = h('button', {
            className: this.options.buttonResetClass,
            onclick: () => { this.setRating(0); }
        }, this.options.buttonResetText!);

        let virtualElement = this.createVirtualRootElement([clearBtn]);
        let domElement = createElement(virtualElement);

        baseElement.appendChild(domElement);
    }

    protected render(): void {
        this.renderLabel();
        this.renderStars();

        if (this.options.buttonElementId) {
            this.renderButton();
        }
    }

    protected rerender(): void {
        let baseElements = [this.element(this.options.labelElementId), this.element(this.options.starsElementId)];

        if (this.options.buttonElementId) {
            baseElements.push(this.element(this.options.buttonElementId!));
        }

        for (let baseElement of baseElements) {
            while (baseElement.firstChild) {
                baseElement.removeChild(baseElement.firstChild);
            }
        }

        this.render();
    }

    public getStarRating(): number {
        let starElementChildren = this.element(this.options.starsElementId).children[0].children;
        let unselectedStarCount = [].filter.call(starElementChildren, (c: any) =>
            c.className.indexOf(this.options.starInactiveClass) > -1).length;

        return this.options.totalAmountOfStars - unselectedStarCount;
    }
}